import gql from 'graphql-tag';

// Global event bus
import Vue from 'vue';
import store from '../store/index';

export const EventBus = new Vue();

const getContentType = (wtype, contentTypes) => {
  for (const [key, type] of Object.entries(contentTypes)) {
    if (type.configs && type.configs.wtype === wtype) {
      return type;
    }
  }
  return null;
};

export const contentsMixin = {
  methods: {
    // Get content types
    getContentType,
    getContentTypes: function () {
      const contentTypesList = Object.keys(this.$store.state.rawContentTypes).map(key => {
        if (this.$store.state.rawContentTypes[key].hideInCTList) {
          return;
        }

        if (!this.$store.state.rawContentTypes[key].configs) {
          return;
        }

        return this.$store.state.rawContentTypes[key];
      });
      return contentTypesList.filter(a => a !== undefined);
    },
    // Gets title field of a content type
    getTitleField: function (contentType, defaultTitle) {
      const { fields } = contentType;
      for (const key of Object.keys(fields)) {
        if (fields[key].isTitle) {
          return key;
        }
      }
      return defaultTitle;
    },
    // Gets header custom fields from specific content type to put in v-data-table header
    getCustomHeaders: function (contentType) {
      const headers = [];
      const fields = contentType ? contentType.fields : {};

      // Generates the header for the v-data-table component with the content type specifications
      for (const key of Object.keys(fields)) {
        // If the field is supposed to be shown in the table header
        if (fields[key].showInList) {
          const newHeader = {
            text: fields[key].label,
            order: fields[key].orderInList,
            value: key
          };

          if (fields[key].options) {
            const optionsMap = {};
            for (const option of fields[key].options) {
              optionsMap[option.value] = option.text;
            }
            newHeader.optionsMap = optionsMap;
          }

          headers.push(newHeader);
        }
      }

      headers.sort((a, b) => a.order - b.order);

      return headers;
    },
    // Gets workflow from content type
    getWorkflowStates: function (content, contentTypes) { // Get the possible workflow states
      const wtype = content.parent_uuid ? content.wtype.replace('_variation', '') : content.wtype;
      const { workflow } = getContentType(wtype, contentTypes).configs;
      return workflow ? store.state.workflows[workflow].states : [];
    },
    // Returns breadcrumbs
    getBreadcrumbs: function (fileId) {
      const breadcrumbs = [{ name: 'Home', path: '/' }];

      // Auxiliar variables
      let path = '/';

      // Creates array with all "folders path"
      const compensated = fileId.split('/');

      // Generates a list of breadcrumbs
      compensated.forEach((value, i) => {
        if (value !== '') { // If element from the split is not empty add it to the breadcrumb
          path += value;
          breadcrumbs.push({ name: value, path: path });
          path += '/';
        }
      });

      return breadcrumbs;
    },
    // Edit content
    editContent: function (content, newTab = false) {
      const uuid = content.parent_uuid ? content.parent_uuid : content.uuid;
      let wtype = content.parent_wtype ? content.parent_wtype : content.wtype;

      if (content.parent_uuid && !content.parent_wtype) { // Fix if we have parent uuid without parent wtype
        wtype = content.wtype.replace('_variation', '');
      }

      // If founded content type goes to a specific route
      if (wtype) {
        const path = { name: 'ContentEditor', params: { type: wtype, uuid: uuid } };

        if (content.parent_uuid) { // ContentEditor with variations
          path.query = { variation: content.uuid };
        }

        // It opens in new tab
        if (newTab) {
          const route = this.$router.resolve(path);
          window.open(route.href, '_blank');
        } else {
          this.$router.push(path);
        }
      }
    },
    // Middleware method to check if user want's to leave from content editor without saving
    checkIfHasChanges: function (acceptMethod, abortMethod = null) {
      // If the current route is the content editor and has changes open's tha pop up alerting for unsaved changes
      if (['ContentEditor'].includes(this.$router.history.current.name) && this.$store.state.hasChanges) {
        EventBus.$emit('unsavedChangesAlert', { acceptMethod: acceptMethod, abortMethod: abortMethod }); // Calls the pop up using an event
      } else {
        acceptMethod();
      }
    },
    defaultField: function (multivalued, viewType, defaultValue) {
      if (defaultValue) {
        return defaultValue;
      }

      if (multivalued) {
        return [];
      }
      if (viewType === 'Switch') {
        return false;
      }
      return null;
    },
    setContent: function (contentType, rawContentTypes, data = {}) {
      data = _.cloneDeep(data);

      // Filter all fields
      for (const [key, field] of Object.entries(contentType.fields)) {
        // If content doesn't is defined
        if (!data.hasOwnProperty(key) || data[key] === null) {
          data[key] = this.defaultField(field.multivalued, field.viewType, field.defaultValue);
        } else if (field.viewType === 'Entity') {
          const contentType = rawContentTypes[field.type];
          if (field.multivalued) {
            data[key] = data[key] && data[key].length ? data[key].filter(val => val !== null).map(v => this.setContent(contentType, rawContentTypes, v)) : [];
          } else {
            data[key] = data[key] ? this.setContent(contentType, rawContentTypes, data[key]) : null;
          }
        } else if (field.viewType === 'Picker' && field.multivalued) {
          data[key] = data[key] && data[key].length ? data[key].filter(v => v !== null) : [];
        }
      }

      return data;
    },
    isFolder: function (hit) {
      return hit.wtype && hit.wtype === 'folder';
    },
    // Checks if the object is a valid JSON and clones it (Converting to JSON and back)
    cloneObject: function (obj) {
      if (obj !== undefined && obj !== null && obj.constructor == Object) {
        return JSON.parse(JSON.stringify(obj));
      }
    },
    difference: function (object, base) {
      function changes (object, base) {
        let arrayIndexCounter = 0;
        return _.transform(object, (result, value, key) => {
          // check the differences between the new and old object
          if (!_.isEqual(value, base[key])) {
            // object tha has change
            const resultKey = _.isArray(base) ? arrayIndexCounter++ : key;
            // if the current values are a object check again, if not gives the value changed
            result[resultKey] = (_.isObject(value) && _.isObject(base[key])) ? changes(value, base[key]) : value;
          }
        });
      }
      return changes(object, base);
    },
    // Formats a date time to a string like: 02/06/2019 à 06:54:32
    formatDateTime: function (dateTime) {
      return new Date(dateTime).toLocaleString('fr-FR');
    },
    // Checks if a string containing a date is valid and not a number
    isValidDateTime: function (date) {
      return !(!isNaN(date) || !(new Date(date) !== 'Invalid Date' && !isNaN(new Date(date))));
    },
    // Transforms date of format 2018-08-27T00:00:00Z into 2018-08-27
    formatDate: function (date) {
      if (date) {
        return date.split('T')[0];
      }
    },
    // Clears keep-alive component cache (clearing all back history)
    clearCacheKeepAlive: function () {
      store.commit('setKeepAlive', false);
      setTimeout(() => {
        store.commit('setKeepAlive', true);
      }, 0);
    }
    // clearCacheFromURL: function (cf_url, amp_url) {
    //   const mutation = `mutation ($cf_url: String, $amp_url: String) {
    //     clear_cf_cache (url: $cf_url)
    //     clear_amp_cache (url: $amp_url)
    //   }`;
    //   console.log(mutation);
    //   this.$apollo.mutate({
    //     mutation: gql(mutation),
    //     variables: {
    //       cf_url: cf_url,
    //       amp_url: amp_url
    //     }
    //   }).then(response => {
    //     console.log('Cache cleared on url', response);
    //   });
    // }
  }
};
